/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { ICustomEvent } from "../../../../store/api/admin/types";
import { useAddEditDeleteCustomEvent } from "./hooks";
import { setBasicDialogOpenFor, setErrorMsg } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { isError } from "../../../../utils/validations";
import { InputField } from "../../../../components/Shared/Radix/Inputs/InputField";
import { Sheet } from "../../../../components/ui/sheet";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Label } from "../../../../components/ui/label";

interface IAddEditCustomEventProps {
  modelOpenMode?: string;
  selectedCustomEvent?: ICustomEvent;
  isCreateCustomEvent?: boolean;
}

const AddEditCustomEvent: React.FC<IAddEditCustomEventProps> = ({ modelOpenMode = "Add", selectedCustomEvent, isCreateCustomEvent = false }) => {
  const dispatch = useStoreDispatch();

  const { formData, setFormData, errMsg, setErrMsg, submitHandler } = useAddEditDeleteCustomEvent(selectedCustomEvent, isCreateCustomEvent);

  const handleChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
    dispatch(setErrorMsg({ ...errMsg, [name]: "" }));
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData({ ...formData, [name]: checked });
  };

  const isEnableSave = !!formData?.name && !!formData?.matchString && !isError(errMsg);

  const handleClose = () => {
    dispatch(setErrorMsg({}));
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose}>
        Cancel
      </Button>
      <Button onClick={submitHandler} disabled={!isEnableSave}>
        Save
      </Button>
    </>
  );

  return (
    <Sheet open={true} onClose={handleClose} title={`${modelOpenMode} Custom Event`} footer={footer} width="560px">
      <form autoComplete="off">
        <div className="space-y-5 pt-2">
          {/* Name */}
          <InputField
            label="Name"
            name="name"
            required
            disabled={!isCreateCustomEvent}
            value={formData?.name || ""}
            onChange={handleChange}
            maxLength={512}
            placeholder="Enter name"
            tooltipText="Maximum length: 512 characters"
             autoFocus={true}
          />

          {/* Description */}
          <InputField
            label="Description"
            name="description"
            value={formData?.description || ""}
            onChange={handleChange}
            maxLength={512}
            placeholder="Enter description"
            tooltipText="Maximum length: 512 characters"
          />

          {/* Pattern */}
          <InputField
            label="Pattern"
            name="matchString"
            required
            value={formData?.matchString || ""}
            onChange={handleChange}
            maxLength={255}
            placeholder="Enter pattern"
            tooltipText="Maximum length: 255 characters"
          />

          <div className="flex items-center gap-6">
            {/* Regex */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="custom-event-regex"
                checked={formData?.regex}
                onCheckedChange={(checked: boolean) => handleCheckboxChange("regex", !!checked)}
              />
              <Label htmlFor="custom-event-regex" className="cursor-pointer">Regex</Label>
            </div>

            {/* Match Case */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="custom-event-matchCase"
                checked={formData?.matchCase}
                onCheckedChange={(checked: boolean) => handleCheckboxChange("matchCase", !!checked)}
              />
              <Label htmlFor="custom-event-matchCase" className="cursor-pointer">Match Case</Label>
            </div>
          </div>
        </div>
      </form>
    </Sheet>
  );
};

export default AddEditCustomEvent;